/**
 * Caisse globale (💵+) — encaisser depuis n'importe quelle page (admin).
 *
 * - Montant en un geste (montants rapides tirés des tarifs, ou le montant dû de l'élève choisi)
 * - Élève en un geste : présents du cours en cours / du jour en premier, puis ceux qui doivent de l'argent, puis recherche
 * - « Attribuer plus tard » : l'argent est noté sans nom, puis attribué depuis la section « À attribuer »
 * Le paiement suit la même logique que le bouton ✓ montant : forfait non soldé d'abord,
 * sinon le cours collectif de la séance, sinon un paiement simple.
 */
import { useEffect, useMemo, useState } from 'react';
import { studentMatches } from '../lib/search';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { X, Search, Clock, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../api';
import { cn } from '../lib/utils';
import { Avatar } from '../pages/Dashboard';
import { Chip, fmtAmount } from './MobileHeader';

const METHODS = [['cash', 'Cash'], ['twint', 'Twint'], ['virement', 'Virement']];
const getLastMethod = () => { try { return localStorage.getItem('bachata_last_method') || 'cash'; } catch { return 'cash'; } };
const setLastMethod = (m) => { try { localStorage.setItem('bachata_last_method', m); } catch { /* ignore */ } };
const fmtTime = (iso) => { try { return format(parseISO(iso), "EEE d MMM HH:mm", { locale: fr }); } catch { return ''; } };
const nowLocal = () => format(new Date(), "yyyy-MM-dd'T'HH:mm");

// Séance de référence : celle du cours affiché, sinon celle en cours / la plus récente du jour, sinon la prochaine du jour
async function findSession(courseId) {
  const today = format(new Date(), 'yyyy-MM-dd');
  const now = format(new Date(), "yyyy-MM-dd'T'HH:mm");
  if (courseId) {
    const course = await api.get(`/group-courses/${courseId}`);
    const sessions = (course.sessions || []).filter(s => !s.is_cancelled);
    const session = sessions.find(s => s.datetime_start.slice(0, 10) === today)
      || sessions.filter(s => s.datetime_start <= now).sort((a, b) => b.datetime_start.localeCompare(a.datetime_start))[0];
    return session ? { session, course } : null;
  }
  const classes = await api.get(`/classes?start=${today}T00:00:00&end=${today}T23:59:59&type=group`).catch(() => []);
  const group = classes.filter(c => c.group_course_id && c.status !== 'cancelled');
  const started = group.filter(c => c.datetime_start <= now).sort((a, b) => b.datetime_start.localeCompare(a.datetime_start));
  const pick = started[0] || group.sort((a, b) => a.datetime_start.localeCompare(b.datetime_start))[0];
  if (!pick) return null;
  const course = await api.get(`/group-courses/${pick.group_course_id}`);
  const session = (course.sessions || []).find(s => s.id === pick.id) || pick;
  return { session, course };
}

export default function Cashbox({ open, onClose, courseId, onChanged, initialAmount = null, pendingOpen = false }) {
  const [students, setStudents] = useState([]);
  const [finance, setFinance] = useState([]);
  const [pending, setPending] = useState([]);
  const [pricing, setPricing] = useState({});
  const [ctx, setCtx] = useState(null);           // { session, course }
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState(getLastMethod);
  const [studentId, setStudentId] = useState(null);
  const [query, setQuery] = useState('');
  const [assigning, setAssigning] = useState(null); // encaissement en attente qu'on est en train d'attribuer
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState('');              // v2.25 : petit détail pour se souvenir (« lunettes »)
  const [showPending, setShowPending] = useState(false);  // v2.25 : « À attribuer » replié par défaut

  const load = async () => {
    const [s, f, p, t] = await Promise.all([
      api.get('/students'), api.get('/finance').catch(() => []), api.get('/cashbox/pending').catch(() => []), api.get('/teacher').catch(() => ({})),
    ]);
    setStudents(s || []); setFinance(f || []); setPending(p || []); setPricing(t?.pricing || {});
    setCtx(await findSession(courseId).catch(() => null));
  };

  useEffect(() => {
    if (!open) return;
    setAmount(initialAmount ? fmtAmount(initialAmount) : ''); setStudentId(null); setQuery(''); setAssigning(null); setMethod(getLastMethod()); setNote(''); setShowPending(pendingOpen);
    load();
  }, [open, courseId, initialAmount, pendingOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const finOf = useMemo(() => Object.fromEntries(finance.map(f => [f.student?.id, f])), [finance]);
  const dueOf = (id) => Math.max(0, Math.round((finOf[id]?.total_remaining || 0) * 100) / 100);
  const byId = useMemo(() => Object.fromEntries(students.map(s => [s.id, s])), [students]);

  // Élèves du cours de la séance : présents en premier (sinon tous les inscrits actifs)
  const tonight = useMemo(() => {
    if (!ctx) return [];
    const enrolled = (ctx.course.enrolled_students || []).filter(e => (e.status || 'active') === 'active').map(e => e.student_id);
    const present = (ctx.session.attendances || []).filter(a => a.present).map(a => a.student_id);
    const ids = present.length ? [...new Set([...present, ...enrolled])] : enrolled;
    return ids.map(id => byId[id]).filter(Boolean);
  }, [ctx, byId]);
  const presentIds = new Set((ctx?.session.attendances || []).filter(a => a.present).map(a => a.student_id));

  const owing = useMemo(() => students.filter(s => dueOf(s.id) > 0 && !tonight.includes(s))
    .sort((a, b) => dueOf(b.id) - dueOf(a.id)), [students, tonight, finOf]); // eslint-disable-line react-hooks/exhaustive-deps

  const searched = useMemo(() => {
    if (query.trim().length < 2) return null;
    return students.filter(s => studentMatches(s, query)).slice(0, 12);
  }, [query, students]);

  const amountChips = useMemo(() => {
    const base = [pricing.group_1h30 || 25, 40, 50, 100, 150, 200];
    return [...new Set(base.map(Number))].sort((a, b) => a - b);
  }, [pricing]);

  if (!open) return null;

  const selected = studentId ? byId[studentId] : null;
  const amountNum = parseFloat(String(amount).replace(',', '.')) || 0;
  const chooseStudent = (id) => {
    setStudentId(id);
    if (!amount && !assigning && dueOf(id) > 0) setAmount(String(dueOf(id)));
  };

  // Où rattacher le paiement : forfait non soldé > cours de la séance (si l'élève y est inscrit) > paiement simple
  const target = (id, fallbackDate) => {
    const pkg = (finOf[id]?.packages || []).find(p => Math.max(0, p.total_price - (p.paid_amount || 0)) > 0);
    if (pkg) return { package_id: pkg.id, group_course_id: null, date: fallbackDate || nowLocal() };
    const inCourse = ctx && (ctx.course.enrolled_students || []).some(e => e.student_id === id);
    if (inCourse) return { package_id: null, group_course_id: ctx.course.id, date: ctx.session.datetime_start };
    return { package_id: null, group_course_id: null, date: fallbackDate || nowLocal() };
  };

  const done = (msg) => { toast.success(msg); setLastMethod(method); onChanged?.(); onClose(); };

  const encaisser = async () => {
    if (!selected || (!assigning && amountNum <= 0)) return;
    setSaving(true);
    try {
      if (assigning) {
        await api.post(`/cashbox/pending/${assigning.id}/assign`, { student_id: selected.id, ...target(selected.id, assigning.created_at.slice(0, 16)) });
        done(`✓ ${assigning.amount} CHF attribués à ${selected.first_name} ${selected.last_name}`);
      } else {
        await api.post('/payments', { student_id: selected.id, amount: amountNum, method, notes: '', ...target(selected.id) });
        done(`✓ ${amountNum} CHF · ${selected.first_name} ${selected.last_name}`);
      }
    } catch { toast.error('Erreur lors de l’encaissement'); } finally { setSaving(false); }
  };

  const plusTard = async () => {
    if (amountNum <= 0) return;
    setSaving(true);
    try {
      await api.post('/cashbox/pending', { amount: amountNum, method, notes: note.trim(), class_id: ctx?.session.id || null, group_course_id: ctx?.course.id || null });
      done(`${amountNum} CHF noté${note.trim() ? ` (${note.trim()})` : ''} — à attribuer plus tard`);
    } catch { toast.error('Erreur'); } finally { setSaving(false); }
  };

  const removePending = async (p) => {
    if (!window.confirm(`Supprimer l'encaissement de ${p.amount} CHF ?`)) return;
    await api.delete(`/cashbox/pending/${p.id}`);
    setPending(list => list.filter(x => x.id !== p.id)); onChanged?.();
  };

  const Tile = ({ s }) => {
    const due = dueOf(s.id);
    const match = assigning && due > 0 && Math.abs(due - assigning.amount) < 0.01;
    return (
      <button onClick={() => chooseStudent(s.id)} data-testid="cashbox-student"
        className={cn('flex items-center gap-2 p-2 rounded-xl border text-left min-w-0 transition-colors',
          studentId === s.id ? 'border-[#26A69A] bg-[#26A69A]/10' : 'border-slate-100 bg-white active:bg-slate-50')}>
        <Avatar name={`${s.first_name} ${s.last_name}`} size="sm" />
        <div className="min-w-0 leading-tight">
          <div className="text-sm font-semibold text-slate-900 truncate">{s.first_name} {s.last_name?.[0]}.</div>
          <div className={cn('text-xs truncate', match ? 'text-amber-600 font-bold' : due > 0 ? 'text-slate-500' : 'text-[#26A69A]')}>
            {match ? `≈ ${due} CHF` : due > 0 ? `doit ${due}` : 'à jour'}{presentIds.has(s.id) ? ' · présent' : ''}
          </div>
        </div>
      </button>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end lg:items-center lg:justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30" />
      <div onClick={e => e.stopPropagation()} data-testid="cashbox"
        className="relative w-full lg:max-w-lg max-h-[92vh] flex flex-col bg-slate-50 rounded-t-3xl lg:rounded-3xl shadow-2xl">
        {/* En-tête : montant + moyen */}
        <div className="bg-white rounded-t-3xl px-4 pt-3 pb-3 border-b border-slate-100">
          <div className="w-10 h-1 bg-slate-200 rounded-full mx-auto mb-2 lg:hidden" />
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-extrabold text-slate-900 font-manrope">
              {assigning ? `Attribuer ${assigning.amount} CHF${assigning.notes ? ` · ${assigning.notes}` : ''}` : 'Encaisser'}
            </h2>
            <button onClick={onClose} aria-label="Fermer" className="w-9 h-9 flex items-center justify-center rounded-xl text-slate-500 active:bg-slate-100"><X size={20} /></button>
          </div>
          {!assigning && (
            <>
              <div className="flex items-center gap-2 mb-2">
                <input value={amount} onChange={e => setAmount(e.target.value.replace(/[^\d.,]/g, ''))} inputMode="decimal"
                  placeholder="0" data-testid="cashbox-amount"
                  className="w-32 h-12 px-3 rounded-xl border border-slate-200 text-2xl font-extrabold text-slate-900 text-right outline-none focus:border-[#26A69A]" />
                <span className="text-lg font-bold text-slate-500">CHF</span>
                <input value={note} onChange={e => setNote(e.target.value)} maxLength={60}
                  placeholder="Note (ex. lunettes)" data-testid="cashbox-note"
                  className="flex-1 min-w-0 h-12 px-3 rounded-xl border border-slate-200 text-base text-slate-900 outline-none focus:border-amber-400" />
              </div>
              <div className="flex gap-2 overflow-x-auto no-scrollbar mb-2">
                {selected && dueOf(selected.id) > 0 && (
                  <Chip active={amountNum === dueOf(selected.id)} color="#26A69A" onClick={() => setAmount(String(dueOf(selected.id)))}>Dû {dueOf(selected.id)}</Chip>
                )}
                {amountChips.map(a => <Chip key={a} active={amountNum === a} color="#334155" onClick={() => setAmount(String(a))}>{a}</Chip>)}
              </div>
              <div className="flex gap-2">
                {METHODS.map(([v, l]) => <Chip key={v} active={method === v} onClick={() => setMethod(v)}>{l}</Chip>)}
              </div>
            </>
          )}
        </div>

        {/* Choix de l'élève */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
          {!assigning && pending.length > 0 && (
            <div>
              <button onClick={() => setShowPending(v => !v)} data-testid="cashbox-pending-toggle"
                className="w-full flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-amber-600 mb-1.5">
                À attribuer ({pending.length} · {fmtAmount(pending.reduce((t, p) => t + (p.amount || 0), 0))} CHF)
                <span className="ml-auto text-amber-500">{showPending ? '▲' : '▼'}</span>
              </button>
              {showPending && <div className="space-y-1.5">
                {pending.map(p => (
                  <div key={p.id} className="flex items-center gap-2 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2" data-testid="cashbox-pending">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-slate-900">{p.amount} CHF · {p.method}</div>
                      <div className="text-xs text-slate-500"><span className="capitalize">{fmtTime(p.created_at)}</span>
                        {p.notes && <span className="ml-1 font-semibold text-amber-700" data-testid="cashbox-pending-note">· {p.notes}</span>}</div>
                    </div>
                    <button onClick={() => { setAssigning(p); setStudentId(null); }} className="h-9 px-3 rounded-lg bg-amber-500 text-white text-sm font-bold"
                      data-testid="cashbox-assign-btn">Attribuer</button>
                    <button onClick={() => removePending(p)} aria-label="Supprimer" className="w-9 h-9 flex items-center justify-center rounded-lg text-slate-400"><Trash2 size={16} /></button>
                  </div>
                ))}
              </div>}
            </div>
          )}

          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Qui ? Nom ou téléphone…" data-testid="cashbox-search"
              className="w-full h-11 pl-9 pr-3 rounded-xl border border-slate-200 bg-white text-base outline-none focus:border-[#26A69A]" />
          </div>

          {searched ? (
            <div className="grid grid-cols-2 gap-2">{searched.map(s => <Tile key={s.id} s={s} />)}</div>
          ) : (
            <>
              {tonight.length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">
                    <Clock size={12} /> {ctx.course.name} · {fmtTime(ctx.session.datetime_start)}
                  </div>
                  <div className="grid grid-cols-2 gap-2">{tonight.map(s => <Tile key={s.id} s={s} />)}</div>
                </div>
              )}
              {owing.length > 0 && (
                <div>
                  <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">Doivent de l'argent</div>
                  <div className="grid grid-cols-2 gap-2">{owing.slice(0, 10).map(s => <Tile key={s.id} s={s} />)}</div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Validation */}
        <div className="bg-white border-t border-slate-100 px-4 pt-3 flex gap-2 lg:rounded-b-3xl"
          style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}>
          {assigning ? (
            <button onClick={() => { setAssigning(null); setStudentId(null); }} className="h-12 px-4 rounded-xl bg-slate-100 text-slate-600 font-semibold">Retour</button>
          ) : (
            <button onClick={plusTard} disabled={amountNum <= 0 || saving} data-testid="cashbox-later"
              className="h-12 px-3 rounded-xl bg-slate-100 text-slate-700 font-semibold text-sm disabled:opacity-40">Attribuer plus tard</button>
          )}
          <button onClick={encaisser} disabled={!selected || (!assigning && amountNum <= 0) || saving} data-testid="cashbox-submit"
            className="flex-1 h-12 rounded-xl bg-[#26A69A] text-white font-bold disabled:opacity-40 truncate px-2">
            {saving ? '…' : selected
              ? `${assigning ? 'Attribuer' : `Encaisser ${amountNum || ''} CHF`} · ${selected.first_name}`
              : 'Choisis un élève'}
          </button>
        </div>
      </div>
    </div>
  );
}
